// WebSocket сервис для чата, использующий токен из основного проекта
class ChatWebSocketService {
  constructor() {
    this.ws = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.isConnected = false;
    this.token = null;
    this.isAuthenticated = false;
    this.messageQueue = [];
    this.authTimeout = null;
    this.authFailed = false; // флаг: токен отклонён сервером — не реконнектить
  }

  setToken(token) {
    this.token = token;
    // Новый токен — сбрасываем флаг ошибки аутентификации, чтобы разрешить переподключение
    this.authFailed = false;
    this.reconnectAttempts = 0;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.authenticate();
    }
  }

  connect() {
    // Если токен был отклонён сервером — не пытаться переподключаться
    if (this.authFailed) {
      console.warn('⚠️ [ChatWebSocket] Auth failed, skipping connect until new token is set');
      return;
    }

    // Проверяем, не подключены ли мы уже
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      console.log('🔌 [ChatWebSocket] Already connected or connecting, skipping');
      return;
    }

    // Закрываем старое соединение, если оно есть
    if (this.ws) {
      try {
        this.ws.close();
      } catch (e) {
        // Игнорируем ошибки при закрытии
      }
      this.ws = null;
    }

    // Получаем токен из localStorage
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('⚠️ [ChatWebSocket] No token available');
      return;
    }
    this.token = token;

    // WebSocket — тот же хост, что и страница (nginx проксирует на нужный порт)
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = typeof window !== 'undefined' ? window.location.host : 'localhost';
    const wsUrl = import.meta.env.VITE_CHAT_WS_URL
      ?? (import.meta.env.DEV ? `${protocol}//${host}` : `${protocol}//${host}/chat-ws`);

    console.log('🔌 [ChatWebSocket] Connecting to:', wsUrl);

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('✅ [ChatWebSocket] Connection opened');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.isAuthenticated = false;
      clearTimeout(this.authTimeout);

      if (this.token) {
        this.authenticate();
        this.authTimeout = setTimeout(() => {
          if (!this.isAuthenticated && this.ws?.readyState === WebSocket.OPEN) {
            console.warn('⚠️ [ChatWebSocket] Auth timeout, closing');
            this.ws.close();
            // Не вызываем attemptReconnect — onclose сделает это сам
          }
        }, 10000);
      } else {
        console.warn('⚠️ [ChatWebSocket] Connected but no token set yet');
      }
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📥 [ChatWebSocket] Message received:', data);

        if (data.type === 'auth_success') {
          console.log('✅ [ChatWebSocket] Authentication successful!');
          this.isAuthenticated = true;
          clearTimeout(this.authTimeout);
          this.flushMessageQueue();
        }

        if (data.type === 'auth_error') {
          console.error('❌ [ChatWebSocket] Auth rejected by server, stopping reconnects');
          this.authFailed = true;
          this.isAuthenticated = false;
          clearTimeout(this.authTimeout);
          this.reconnectAttempts = this.maxReconnectAttempts; // блокируем attemptReconnect
          if (this.ws) {
            this.ws.close(1000, 'Auth error');
          }
        }

        this.notifyListeners(data.type, data);
      } catch (error) {
        console.error('❌ [ChatWebSocket] Failed to parse message:', error, event.data);
      }
    };

    this.ws.onclose = (event) => {
      console.log(`🔌 [ChatWebSocket] Connection closed:`, {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      });
      this.isConnected = false;
      clearTimeout(this.authTimeout);
      if (!this.authFailed) {
        this.attemptReconnect();
      }
    };

    this.ws.onerror = (error) => {
      console.error('❌ [ChatWebSocket] Error:', error);
    };
  }

  authenticate() {
    if (!this.token) {
      console.warn('⚠️ [ChatWebSocket] No token available for authentication');
      return;
    }

    if (this.ws?.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ [ChatWebSocket] Cannot authenticate: WebSocket not open');
      return;
    }

    console.log('🔑 [ChatWebSocket] Sending authentication');
    this.ws.send(JSON.stringify({
      type: 'auth',
      token: this.token
    }));
  }

  sendMessage(receiverId, content, messageId, createdAt, sender, messageType = 'text', imageData = null, groupId = null, inReplyToId = null, audioData = null, channelId = null, callbackData = null) {
    const message = {
      receiverId,
      groupId,
      channelId,
      messageId,
      content,
      messageType,
      imageData,
      audioData,
      callbackData,
      sender,
      createdAt,
      inReplyToId
    };

    if (!this.isAuthenticated) {
      console.log('⏳ [ChatWebSocket] Queueing message until authenticated');
      this.messageQueue.push(message);
      return false;
    }

    console.log('📤 [ChatWebSocket] Sending message:', {
      receiverId,
      groupId,
      messageId,
      messageType,
      contentLength: content?.length || 0,
      hasImage: !!imageData,
      sender: sender
    });

    return this._sendRaw(message);
  }

  _sendRaw(message) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return false;
    }
    this.ws.send(JSON.stringify({ type: 'message', ...message }));
    return true;
  }

  flushMessageQueue() {
    console.log(`📤 [ChatWebSocket] Flushing ${this.messageQueue.length} queued messages`);
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this._sendRaw(message);
    }
  }

  sendTyping(receiverId, isTyping = true) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'typing',
        receiverId,
        isTyping
      }));
    }
  }

  on(type, handler) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, []);
    }
    this.listeners.get(type).push(handler);
    console.log(`🎧 [ChatWebSocket] Registered listener for type '${type}'. Total listeners: ${this.listeners.get(type).length}`);

    return () => this.off(type, handler);
  }

  off(type, handler) {
    if (this.listeners.has(type)) {
      const handlers = this.listeners.get(type);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
        console.log(`🗑️ [ChatWebSocket] Removed listener for type '${type}'. Remaining listeners: ${handlers.length}`);
      }
    }
  }

  notifyListeners(type, data) {
    if (this.listeners.has(type)) {
      const handlers = this.listeners.get(type);
      console.log(`🔔 [ChatWebSocket] Calling ${handlers.length} listeners for type '${type}'`);
      handlers.forEach(handler => handler(data));
    }
  }

  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('❌ [ChatWebSocket] Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1);

    console.log(`🔄 [ChatWebSocket] Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      console.log('🔄 [ChatWebSocket] Reconnecting...');
      this.connect();
    }, delay);
  }

  disconnect() {
    clearTimeout(this.authTimeout);
    if (this.ws) {
      this.ws.close(1000, 'User initiated disconnect');
      this.ws = null;
      this.isConnected = false;
      this.isAuthenticated = false;
      // НЕ очищаем listeners — они должны пережить переподключение.
      // Компоненты (BottomNavigation, ChatNotificationPopup) остаются
      // подписанными и продолжат получать события после reconnect.
    }
  }

  // Полная очистка — вызывать только при выходе из приложения
  destroy() {
    if (this.ws) {
      this.ws.close(1000, 'Destroy');
      this.ws = null;
    }
    this.isConnected = false;
    this.listeners.clear();
  }
}

export default new ChatWebSocketService();
